// API endpoints for accounts
import { api } from './client';
import type { Account } from '../../entities/account/model/types';

export interface CreateAccountRequest {
  accessToken: string;
}

export interface CreateAccountFromOAuthRequest {
  code: string;
  redirectUri: string;
}

export interface CreateAccountResponse {
  account: Account;
}

export interface GetAccountsResponse {
  accounts: Account[];
}

export interface GetAccountResponse {
  account: Account;
}

export interface UpdateAccountRequest {
  name?: string;
  isActive?: boolean;
  defaultTemplateId?: string;
  tags?: string[];
}

export interface VerifyAccountResponse {
  valid: boolean;
  account?: Account;
  instagramData?: {
    id: string;
    username: string;
    name?: string;
    account_type?: string;
  };
  error?: string;
}

export const accountsApi = {
  /**
   * Получить все аккаунты
   */
  getAll: (signal?: AbortSignal) => api.get<GetAccountsResponse>('/accounts', { signal }),
  
  /**
   * Получить аккаунт по ID
   */
  getById: (id: string) => api.get<GetAccountResponse>(`/accounts/${id}`),
  
  /**
   * Создать новый аккаунт
   */
  create: (data: CreateAccountRequest) =>
    api.post<CreateAccountResponse>('/accounts', data),

  /**
   * Создать аккаунт через Instagram OAuth (обменивает code на токен на бэкенде)
   */
  createFromOAuth: (data: CreateAccountFromOAuthRequest) =>
    api.post<CreateAccountResponse>('/accounts/instagram-oauth', data),

  /**
   * Обновить токен существующего аккаунта через Instagram OAuth
   */
  refreshTokenViaOAuth: (id: string, data: CreateAccountFromOAuthRequest) =>
    api.post<GetAccountResponse>(`/accounts/${id}/oauth-refresh`, data),
  
  /**
   * Обновить аккаунт
   */
  update: (id: string, data: UpdateAccountRequest) =>
    api.put<GetAccountResponse>(`/accounts/${id}`, data),
  
  /**
   * Удалить аккаунт
   */
  delete: (id: string) => api.delete<{ success: boolean }>(`/accounts/${id}`),
  
  /**
   * Проверить токен аккаунта
   */
  verify: (id: string) => 
    api.post<VerifyAccountResponse>(`/accounts/${id}/verify`),
  
  /**
   * Обновить аватарки всех аккаунтов
   */
  refreshAll: () =>
    api.post<{ success: boolean; total: number; successCount: number; results: any[] }>('/accounts/refresh-all'),

  /**
   * Тихо обновить токены, истекающие в ближайшие 7 дней
   */
  autoRefresh: () =>
    api.post<{ refreshed: number; skipped: number; failed: number }>('/accounts/auto-refresh'),

  /**
   * Скопировать аватарки аккаунтов в наш Storage. Доступно всем ролям:
   * подписанные ссылки Instagram живут ~2 недели, и после этого аватарки
   * пропадают у любого сотрудника, а не только у админа.
   */
  mirrorAvatars: () =>
    api.post<{ mirrored: number; skipped: number; failed: number }>('/accounts/mirror-avatars'),
  
  /**
   * Обновить данные одного аккаунта (аватарка, подписчики и т.д.)
   */
  refresh: (id: string) =>
    api.post<VerifyAccountResponse>(`/accounts/${id}/verify`),
};